/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { getKorDate, getDay, getTimeString } from "utils/date";
import { TRestrictRequest } from "apis/meetings/block-request";
import { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";

type TableRowProps = {
  data: TRestrictRequest;
  onClick: () => void;
  onCancel: () => void;
};

const TableRow = ({ data, onClick, onCancel }: TableRowProps) => {
  const requestReasonRef = useRef<HTMLDivElement>(null);
  const [tooltipWidth, setTooltipWidth] = useState<number>(0);

  useEffect(() => {
    const el = requestReasonRef.current;
    if (!el) return;
    const update = () => setTooltipWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  return (
    <Container
      onClick={(e: any) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4, width: 266 }}>
        <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-label-neutral)">
          {data.meetingRoom.name}
        </UI.Typography>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <UI.Icon name="office" color="var(--alias-color-label-assistive)" size={16} />
            <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
              위치
            </UI.Typography>
          </div>
          <UI.Typography type="label01" fontStyle="regular">
            {data.meetingRoom.building} - {data.meetingRoom.floor}
          </UI.Typography>
        </div>
      </div>
      <div style={{ width: 80, alignItems: "flex-start", justifyItems: "flex-start" }}>
        {data.status === "PENDING" && <UI.Tag size="small" color="gray" label="승인 대기" />}
        {data.status === "APPROVED" && <UI.Tag size="small" color="skyblue" label="승인" />}
        {data.status === "REJECTED" && <UI.Tag size="small" color="pink" label="반려" />}
      </div>
      <div style={{ width: 180, alignItems: "center" }}>
        <UI.Typography type="label01" fontStyle="regular">
          {getKorDate(data.startDateTime.toDateString()).replaceAll(".", ". ")} (
          {getDay(data.startDateTime.toDateString())}) {getTimeString(data.startDateTime.toString())}
          <br />- {getKorDate(data.endDateTime.toDateString()).replaceAll(".", ". ")} (
          {getDay(data.endDateTime.toDateString())}) {getTimeString(data.endDateTime.toString())}
        </UI.Typography>
      </div>
      <TooltipContainer ref={requestReasonRef}>
        <UI.ToolTip
          styles={{
            zIndex: "auto",
          }}
          position={{ top: "100%", left: 0 }}
          type="white"
          text={<p style={{ width: tooltipWidth ? `${tooltipWidth}px` : undefined }}>{data.requestReason}</p>}
        >
          <TwoLineEllipsis>
            <UI.Typography type="label01" fontStyle="regular">
              {data.requestReason}
            </UI.Typography>
          </TwoLineEllipsis>
        </UI.ToolTip>
      </TooltipContainer>
      {data.status !== "PENDING" && <div style={{ width: 74 }}></div>}
      {data.status === "PENDING" && (
        <div
          style={{ width: 74 }}
          onClick={(e: any) => {
            e.stopPropagation();
            onCancel();
          }}
        >
          <UI.Button title="취소하기" size="small" type="solid" primary={false} />
        </div>
      )}
    </Container>
  );
};

export default TableRow;

const Container = styled.div`
  display: flex;
  padding: 12px 24px;
  align-items: center;
  border-bottom: 1px solid var(--alias-color-line-normal);
  gap: 20px;
  cursor: pointer;
  &:hover {
    background-color: var(--alias-color-background-elevated-alternative);
  }
`;

const TwoLineEllipsis = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  flex: 1;
`;

const TooltipContainer = styled.div`
  flex: 1;
  .tooltip {
    z-index: 100;
  }
`;
